'use client'

import { useState } from 'react'

import type { AbstractImage } from '@mntn-dev/ui-utilities'
import type { Person } from '../../types/person.ts'
import { type TestIds, getTestProps } from '../../utils'
import { Avatar } from '../avatar/index.ts'
import { Button } from '../buttons/button.tsx'
import { Stack } from '../stack/stack.tsx'
import { Surface } from '../surface/surface.tsx'
import type { TextareaFormProps } from '../textarea-form/textarea-form.tsx'
import type { TextareaFormValues } from '../textarea-form/use-textarea-form.tsx'
import { Textarea } from '../textarea/textarea.tsx'

type FeedCommentInputProps = Readonly<
  TestIds &
    Pick<TextareaFormProps, 'onSubmit'> & {
      cancelText?: string
      onSubmit: (values: TextareaFormValues) => void
      onCancel: () => void
      requiredErrorText?: string
      submitText?: string
      submitting?: boolean
      user: Person
      image: AbstractImage
    }
>

const FeedCommentInput = ({
  cancelText = 'Cancel',
  dataTestId,
  dataTrackingId,
  onSubmit,
  onCancel,
  // requiredErrorText = 'Comment is required',
  submitText = 'Submit',
  submitting,
  user,
  image,
}: FeedCommentInputProps) => {
  const [comment, setComment] = useState<string>('')

  const handleCancel = () => {
    setComment('')
    onCancel()
  }

  const handleSubmit = () => {
    if (comment.length === 0) {
      return
    }

    onSubmit({ value: comment })
    setComment('')
  }

  return (
    <Surface border padding="8">
      <Stack gap="4" width="full">
        <Avatar size="sm">
          <Avatar.Person person={user} image={image} />
        </Avatar>
        <Stack gap="4" direction="col" grow={true}>
          <Textarea
            className="h-36 resize-none overflow-hidden"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            autoFocus
            {...getTestProps({
              dataTestId: dataTestId ? `${dataTestId}-textarea` : undefined,
              dataTrackingId: dataTrackingId
                ? `${dataTrackingId}-textarea`
                : undefined,
            })}
          />
          <Stack justifyContent="between">
            <Button
              // type="submit"
              disabled={submitting}
              width="48"
              onClick={handleSubmit}
              {...getTestProps({
                dataTestId: dataTestId ? `${dataTestId}-submit` : undefined,
                dataTrackingId: dataTrackingId
                  ? `${dataTrackingId}-submit`
                  : undefined,
              })}
            >
              {submitText}
            </Button>
            <Button
              variant="text"
              {...getTestProps({
                dataTestId: dataTestId ? `${dataTestId}-cancel` : undefined,
                dataTrackingId: dataTrackingId
                  ? `${dataTrackingId}-cancel`
                  : undefined,
              })}
              onClick={handleCancel}
            >
              {cancelText}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Surface>
  )
}

export { FeedCommentInput, type FeedCommentInputProps }
